import React, { useContext, useEffect, useState } from "react";
import { Bounce, toast, ToastContainer } from "react-toastify";
import { BuildDetail } from "./models/BuildDetails";
import { getBuildDetails } from "./services/systemService";
import { showError } from "./services/utilities/ServiceError";

export type GlobalContextType = {
    controlPanelButton: string | undefined;
    setControlPanelButton: React.Dispatch<React.SetStateAction<string | undefined>>;
    refreshDashboard: boolean;
    setRefreshDashboard: React.Dispatch<React.SetStateAction<boolean>>;
    loadBuildInfo: () => void;
    buildInfo: BuildDetail;
    /**
       Some help topics need to be shown based on record id.
       This can only happen if the record id is static and can't be reused.
       Example: for template/default records that can't be deleted such as Reports or 
       Presets.
   */
    setSpecificHelpTopicKey: React.Dispatch<React.SetStateAction<string | undefined>>;
    specificHelpTopicKey?: string;
};

export const GlobalContext = React.createContext<GlobalContextType | undefined>(undefined);

export const useGlobalState = () => useContext(GlobalContext);

type ProvidersProps = {
    children: React.ReactNode;
};

const Providers = ({ children }: ProvidersProps) => {
    const [controlPanelButton, setControlPanelButton] = useState<string | undefined>(undefined);
    const [refreshDashboard, setRefreshDashboard] = useState<boolean>(false);
    const [buildInfo, setBuildInfo] = useState<BuildDetail>({});
    const [specificHelpTopicKey, setSpecificHelpTopicKey] = useState<string | undefined>(undefined);

    useEffect(() => {
        loadBuildInfo();
    }, []);

    const loadBuildInfo = async () => {
        try {
            const buildInfo = await getBuildDetails();
            // Save this on session storage to prevent page refresh from resetting the value.
            window.sessionStorage.setItem("showTabCounts", buildInfo.buildDetail.showTabCounts ? "1" : "0");
            setBuildInfo(buildInfo.buildDetail);
        } catch (error) {
            showError(error, toast.warn);
        }
    }

    return (
        <GlobalContext.Provider
            value={{
                controlPanelButton: controlPanelButton, // used to check which button the user pressed on control panel
                setControlPanelButton: setControlPanelButton,// used to set button the user pressed on control panel
                refreshDashboard,
                setRefreshDashboard,
                loadBuildInfo,
                buildInfo,
                setSpecificHelpTopicKey,
                specificHelpTopicKey
            }}>
            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick={false}
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
                transition={Bounce} />
            {children}
        </GlobalContext.Provider>
    );
};

export default Providers;